import React from 'react'
import styled from 'styled-components'
import { darken } from 'polished'
import { useTranslation } from 'react-i18next'
import { NavLink, Link as HistoryLink } from 'react-router-dom'

import { ArrowLeft } from 'react-feather'
import { RowBetween } from '../Row'
import QuestionHelper from '../QuestionHelper'
import stockIcon from '../../assets/svg/stock.svg'
import { useKlineModalOpen, useKlineActions, useCurrentTradingPair } from '../../state/kline/hooks'
import Modal from '../Modal'
import KlineModal from '../KlineModal'

const Tabs = styled.div`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  border-radius: 3rem;
  justify-content: space-evenly;
`

const activeClassName = 'ACTIVE'

const StyledNavLink = styled(NavLink).attrs({
  activeClassName
})`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: center;
  height: 3rem;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  font-size: 20px;

  &.${activeClassName} {
    border-radius: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.text1};
  }

  :hover,
  :focus {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`

const StyledExternalLink = styled.a`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: center;
  justify-content: center;
  height: 3rem;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text3};
  font-size: 20px;

  :hover,
  :focus {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`

const ActiveText = styled.div`
  font-weight: 500;
  font-size: 20px;
`

const StyledArrowLeft = styled(ArrowLeft)`
  color: ${({ theme }) => theme.text1};
`

export function SwapPoolTabs({ active }: { active: 'swap' | 'pool' }) {
  const { t } = useTranslation()
  const isModalOpen = useKlineModalOpen()
  const { openModal, closeModal } = useKlineActions()
  const currentTradingPair = useCurrentTradingPair()
  const { clearPair } = useKlineActions()

  const toggleModal = () => {
    if (isModalOpen) {
      closeModal()
    } else {
      openModal()
    }
  }

  return (
    <Tabs style={{ marginBottom: '20px' }}>
      <StyledNavLink id={`swap-nav-link`} to={'/swap'} isActive={() => active === 'swap'}>
        {t('swap')}
      </StyledNavLink>
      <StyledNavLink id={`pool-nav-link`} to={'/pool'} isActive={() => active === 'pool'} onClick={() => clearPair()}>
        {t('pool')}
      </StyledNavLink>
      <StyledExternalLink
        id={`bridge-nav-link`}
        onClick={e => {
          e.preventDefault()
          window.location.href = 'https://x-kbridge.linksay.io'
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        }}
      >
        {t('bridge')}
      </StyledExternalLink>
      {currentTradingPair && (
        <img src={stockIcon} alt="" style={{ width: '30px', cursor: 'pointer' }} onClick={toggleModal} />
      )}

      <Modal isOpen={isModalOpen} onDismiss={closeModal} maxHeight={90} minHeight={false}>
        <KlineModal onClose={closeModal} />
      </Modal>
    </Tabs>
  )
}

export function FindPoolTabs() {
  const { t } = useTranslation()
  return (
    <Tabs>
      <RowBetween style={{ padding: '1rem' }}>
        <HistoryLink to="/pool">
          <StyledArrowLeft />
        </HistoryLink>
        <ActiveText>{t('importPool')}</ActiveText>
        <QuestionHelper text={t('importPoolDescription')} />
      </RowBetween>
    </Tabs>
  )
}

export function AddRemoveTabs({ adding }: { adding: boolean }) {
  const { t } = useTranslation()
  return (
    <Tabs>
      <RowBetween style={{ padding: '1rem' }}>
        <HistoryLink to="/pool">
          <StyledArrowLeft />
        </HistoryLink>
        <ActiveText>
          {adding ? t('add') : t('remove')} {t('liquidity')}
        </ActiveText>
        <QuestionHelper text={adding ? t('addLiquidityDescription') : t('removeLiquidityDescription')} />
      </RowBetween>
    </Tabs>
  )
}
